import {FC, useEffect, useRef, useState} from 'react'
import {useHistory} from 'react-router-dom'
import {useLayout} from '../../../_metronic/layout/core'
import {showErrorAlert} from '../../helpers/alert'
import {handleApiError} from '../../utils/handleApiError'
import {useProfessionalInterestsField} from '../../hooks/components/useProfessionalInterestsField'
import {
  getMyPartnerExpertProfile,
  saveMyPartnerExpertProfile,
} from '../../services/partnerExpertService'
import {
  buildPayload,
  EMPTY_FORM,
  formFromProfile,
  PartnerExpertForm,
  toggleArea,
} from './partnerExpert'

// WP-X8 — partner-expert registration/edit form: bio, rate_type + listed_rate, expertise areas,
// availability_note, is_listed (+ headline/currency/active). PUTs to #46's upsert endpoint.
// BLOCKED on #46 deploy for verification.
const PartnerExpertRegistrationPage: FC = () => {
  const history = useHistory()
  const {config, setLayout} = useLayout()
  const restoreRef = useRef(config)
  const {collection: areas} = useProfessionalInterestsField()

  const [form, setForm] = useState<PartnerExpertForm>(EMPTY_FORM)
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    restoreRef.current = config
    setLayout({
      ...config,
      header: {...config.header, display: true},
      aside: {...config.aside, display: true},
      toolbar: {...config.toolbar, display: false},
      footer: {...config.footer, display: true},
    })
    return () => setLayout(restoreRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Prefill from the existing profile (edit); a 404 just leaves the blank form (first registration).
  useEffect(() => {
    let active = true
    getMyPartnerExpertProfile()
      .then((data) => active && setForm(formFromProfile(data)))
      .catch((err) => {
        if (!active) return
        if (err?.response?.status === 403) setForbidden(true)
      })
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [])

  const set = (patch: Partial<PartnerExpertForm>) => setForm((f) => ({...f, ...patch}))

  const handleSubmit = async () => {
    try {
      setSaving(true)
      await saveMyPartnerExpertProfile(buildPayload(form))
      showErrorAlert({title: 'Saved', message: 'Your expert profile has been saved.'})
      history.push('/partner/expert')
    } catch (err) {
      handleApiError(err, {
        onServerError: () =>
          showErrorAlert({title: 'Error', message: 'Could not save your profile right now.'}),
        onBadRequest: () =>
          showErrorAlert({title: 'Error', message: 'Please review the details and try again.'}),
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className='text-center py-10'>Loading…</div>
  if (forbidden)
    return <div className='text-center py-10 text-muted'>This area is for partner accounts.</div>

  return (
    <div className='partner-expert-registration'>
      <button
        type='button'
        className='nb-link-underline-500 mb-6'
        onClick={() => history.push('/partner/expert')}
      >
        ← Back
      </button>

      <div className='card p-8' style={{maxWidth: 760}}>
        <h1 className='mb-6'>Expert profile</h1>

        <div className='mb-4'>
          <label className='form-label'>Headline</label>
          <input
            className='form-control'
            placeholder='e.g. Cross-border M&A advisory'
            value={form.headline}
            onChange={(e) => set({headline: e.target.value})}
          />
        </div>

        <div className='mb-4'>
          <label className='form-label'>Bio</label>
          <textarea
            className='form-control'
            rows={4}
            value={form.bio}
            onChange={(e) => set({bio: e.target.value})}
          />
        </div>

        <div className='d-flex flex-wrap gap-4'>
          <div className='mb-4'>
            <label className='form-label'>Listed rate</label>
            <input
              type='number'
              className='form-control'
              value={form.listedRate}
              onChange={(e) => set({listedRate: e.target.value})}
            />
          </div>
          <div className='mb-4' style={{width: 120}}>
            <label className='form-label'>Currency</label>
            <input
              className='form-control'
              value={form.currency}
              onChange={(e) => set({currency: e.target.value})}
            />
          </div>
          <div className='mb-4'>
            <label className='form-label'>Rate type</label>
            <input
              className='form-control'
              placeholder='e.g. hour, day, project'
              value={form.rateType}
              onChange={(e) => set({rateType: e.target.value})}
            />
          </div>
        </div>

        <div className='mb-4'>
          <label className='form-label'>Availability note</label>
          <input
            className='form-control'
            placeholder='e.g. Weekdays, remote'
            value={form.availabilityNote}
            onChange={(e) => set({availabilityNote: e.target.value})}
          />
        </div>

        <div className='mb-4'>
          <label className='form-label'>Expertise areas</label>
          <div className='d-flex flex-wrap gap-2'>
            {areas.map((a: any) => {
              const selected = form.expertiseAreas.includes(a.id)
              return (
                <button
                  key={a.id}
                  type='button'
                  className={`badge ${selected ? 'badge-primary' : 'badge-light-primary'}`}
                  onClick={() => set({expertiseAreas: toggleArea(form.expertiseAreas, a.id)})}
                >
                  {a.name}
                </button>
              )
            })}
            {areas.length === 0 && (
              <span className='text-muted'>No expertise areas available.</span>
            )}
          </div>
        </div>

        <div className='d-flex gap-6 mb-6'>
          <label className='d-flex align-items-center gap-2'>
            <input
              type='checkbox'
              checked={form.isListed}
              onChange={(e) => set({isListed: e.target.checked})}
            />
            <span>Listed (visible in Book an Expert)</span>
          </label>
          <label className='d-flex align-items-center gap-2'>
            <input
              type='checkbox'
              checked={form.active}
              onChange={(e) => set({active: e.target.checked})}
            />
            <span>Active</span>
          </label>
        </div>

        <button className='btn nb-btn-primary' disabled={saving} onClick={handleSubmit}>
          Save profile
        </button>
      </div>
    </div>
  )
}

export default PartnerExpertRegistrationPage
